import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import api from '../../../app/api/api';
import {
  TaxRate,
  TaxRateDetails,
  TaxRateForm,
  TaxRateFormModel,
} from '../../../app/data/models/taxes/taxRate';

interface TaxState {
  taxRates: TaxRate[];
  taxRate: TaxRateDetails | null;
  status: string;
  taxRatesListStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  taxRateDetailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  taxRateCreateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  taxRateUpdateStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  taxRateDeleteStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: TaxState = {
  taxRates: [],
  taxRate: null,
  status: 'idle',
  taxRatesListStatus: 'idle',
  taxRateDetailsStatus: 'idle',
  taxRateCreateStatus: 'idle',
  taxRateUpdateStatus: 'idle',
  taxRateDeleteStatus: 'idle',
};

export const fetchTaxRatesAsync = createAsyncThunk<
  TaxRate[],
  { restaurantId: string }
>('taxes/fetchTaxRatesAsync', async ({ restaurantId }, thunkAPI) => {
  try {
    return await api.TaxRate.list(restaurantId);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkAPI.rejectWithValue({ error: error.response?.data });
    }
    return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const fetchTaxRateDetailsAsync = createAsyncThunk<
  TaxRateDetails,
  { restaurantId: string; taxRateId: string }
>(
  'taxes/fetchTaxRateDetailsAsync',
  async ({ restaurantId, taxRateId }, thunkAPI) => {
    try {
      return await api.TaxRate.details(restaurantId, taxRateId);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const createTaxRateAsync = createAsyncThunk<
  TaxRate,
  { restaurantId: string; data: TaxRateFormModel }
>('taxes/createTaxRateAsync', async ({ restaurantId, data }, thunkAPI) => {
  try {
    return await api.TaxRate.create(restaurantId, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkAPI.rejectWithValue({ error: error.response?.data });
    }
    return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const updateTaxRateAsync = createAsyncThunk<
  TaxRate,
  { restaurantId: string; taxRateId: string; data: TaxRateFormModel }
>(
  'taxes/updateTaxRateAsync',
  async (
    {
      restaurantId,
      taxRateId,
      data,
    }: { restaurantId: string; taxRateId: string; data: TaxRateFormModel },
    thunkAPI
  ) => {
    try {
      return await api.TaxRate.update(restaurantId, taxRateId, data);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue({ error: error.response?.data });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const deleteTaxRateAsync = createAsyncThunk<
  void,
  { restaurantId: string; taxRateId: string }
>('taxes/deleteTaxRateAsync', async ({ restaurantId, taxRateId }, thunkAPI) => {
  try {
    await api.TaxRate.delete(restaurantId, taxRateId);
    return;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      return thunkAPI.rejectWithValue({ error: error.response?.data });
    }
    return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const taxSlice = createSlice({
  name: 'taxes',
  initialState,
  reducers: {
    clearTaxRateDetails: (state) => {
      state.taxRate = null;
      state.taxRateDetailsStatus = 'idle';
    },
    setTaxRates: (state, action) => {
      state.taxRates = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaxRateDetailsAsync.pending, (state) => {
        state.taxRateDetailsStatus = 'loading';
      })
      .addCase(fetchTaxRateDetailsAsync.fulfilled, (state, action) => {
        state.taxRate = action.payload;
        state.taxRateDetailsStatus = 'idle';
      })
      .addCase(fetchTaxRateDetailsAsync.rejected, (state, action) => {
        state.taxRateDetailsStatus = 'idle';
        console.error(action.payload);
      })
      .addMatcher(isAnyOf(fetchTaxRatesAsync.pending), (state) => {
        state.taxRatesListStatus = 'loading';
      })
      .addMatcher(isAnyOf(fetchTaxRatesAsync.fulfilled), (state, action) => {
        state.taxRates = action.payload;
        state.taxRatesListStatus = 'idle';
      })
      .addMatcher(isAnyOf(fetchTaxRatesAsync.rejected), (state, action) => {
        state.taxRatesListStatus = 'idle';
        console.error(action.payload);
      })
      .addMatcher(isAnyOf(createTaxRateAsync.pending), (state) => {
        state.taxRateCreateStatus = 'loading';
      })
      .addMatcher(
        isAnyOf(createTaxRateAsync.fulfilled, createTaxRateAsync.rejected),
        (state) => {
          state.taxRate = null;
          state.taxRateDetailsStatus = 'idle';
          state.taxRateCreateStatus = 'idle';
        }
      )
      .addMatcher(isAnyOf(updateTaxRateAsync.pending), (state) => {
        state.taxRateUpdateStatus = 'loading';
      })
      .addMatcher(
        isAnyOf(updateTaxRateAsync.fulfilled, updateTaxRateAsync.rejected),
        (state) => {
          state.taxRate = null;
          state.taxRateDetailsStatus = 'idle';
          state.taxRateUpdateStatus = 'idle';
        }
      )
      .addMatcher(isAnyOf(deleteTaxRateAsync.pending), (state) => {
        state.taxRateDeleteStatus = 'loading';
      })
      .addMatcher(isAnyOf(deleteTaxRateAsync.fulfilled), (state, action) => {
        const index = state.taxRates.findIndex(
          (rate) => rate.id === action.meta.arg.taxRateId
        );
        if (index !== -1) {
          state.taxRates.splice(index, 1);
        }
        state.taxRateDeleteStatus = 'idle';
      })
      .addMatcher(isAnyOf(deleteTaxRateAsync.rejected), (state, action) => {
        state.taxRateDeleteStatus = 'idle';
        console.error(action.payload);
      });
  },
});

export const { clearTaxRateDetails, setTaxRates } = taxSlice.actions;

export default taxSlice.reducer;
